import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from "@angular/core";
import {
  ClientInformationData,
  DeleteNoteSubject,
  Note,
  UpdateNoteSubject,
} from "./note-models";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { NewNoteModalComponent } from "./new-note-modal/new-note-modal.component";
import { NotesService } from "./notes.service";
import { HotToastService } from "@ngneat/hot-toast";
import { DeleteConfirmationModalComponent } from "../modals/delete-confirmation-modal.component";
import { WorkspacesService } from "../workspaces.service";
import { Company } from "src/app/dashboard/shared-data.service";
import { DashboardService } from "src/app/dashboard/dashboard.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs";
import { NoteCardComponent } from "./note-card/note-card.component";
import { NgClass, NgIf, NgFor } from "@angular/common";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { PaidOverlayDirective } from "../../../paid-overlay.directive";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";

@Component({
    selector: "app-notes",
    templateUrl: "./notes.component.html",
    styleUrls: ["./notes.component.scss"],
    imports: [
        PaidOverlayDirective,
        TooltipModule,
        NgClass,
        NgIf,
        NoteCardComponent,
        NgFor,
        FormsModule,
        BsDropdownModule,
    ]
})
export class NotesComponent implements OnInit {
  loading: boolean = true;
  notes: Array<Note>;
  clientInformation: ClientInformationData; // This is the company default note, the first note on the list
  company: Company;
  sortBy: string = 'newest';

  cdr = inject(ChangeDetectorRef);
  isPanelHidden = false;

  destroyRef = inject(DestroyRef);

  constructor(
    private modalService: BsModalService,
    private notesService: NotesService,
    public bsModalRef: BsModalRef,
    private toastr: HotToastService,
    private workspacesService: WorkspacesService,
    private dashboardService: DashboardService,
  ) {}

  

  ngOnInit(): void {
    this.workspacesService.$company
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((company) => {
        this.loading = true;
        this.notes = [];
        this.clientInformation = null;
        this.cdr.detectChanges();
        this.company = company;

        if (company != null) {
          this.reload();
        }
      });
    this.notesService.updateNoteSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: UpdateNoteSubject) => {
        this.updateNoteOrClientInformation(data);
      });
    this.notesService.deleteNoteSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: DeleteNoteSubject) => {
        if (data) {
          this.deleteNote(data);
        }
      });
  }

  ngOnDestroy(): void {
    
  }

  async getAllCompanyNotes(): Promise<any> {
    const { notes, client_information } = await this.notesService.getAllNotes(
      this.company.uuid,
    );
    return { notes, client_information };
  }

  openNewNoteModal(): void {
    this.bsModalRef = this.modalService.show(NewNoteModalComponent, {
      class: "modal-lg",
    });

    this.bsModalRef.content.onClose.subscribe(async (res: boolean | Note) => {
      if (res && typeof res !== "boolean") {
        const { title, content } = res;
        const newNote = { title, content };
        this.dashboardService.show_loading.next(true);
        try {
          const { message, note } = await this.notesService.createNewNote(
            this.company.uuid,
            newNote,
          );
          this.notes.push(note);
          this.cdr.detectChanges();
          this.toastr.success(`${message}`);
        } catch (error) {
          this.handleErrorCatch(error);
        } finally {
          this.dashboardService.show_loading.next(false);
        }
      }
    });
  }

  deleteNote({ title, uuid }: Note) {
    const initialState = {
      displayName: title,
      isForDeleteNote: true,
    };
    this.bsModalRef = this.modalService.show(DeleteConfirmationModalComponent, {
      initialState,
    });
    this.bsModalRef.content.onClose.subscribe(async (res: boolean) => {
      if (res) {
        this.dashboardService.show_loading.next(true);
        try {
          const { message } = await this.notesService.deleteNote(
            this.company.uuid,
            uuid,
          );
          this.notes = this.notes.filter((note) => note.uuid !== uuid);
          this.notesService.hideEditModalSubject.next(true);
          this.toastr.success(`${message}`);
        } catch (error) {
          this.handleErrorCatch(error);
        } finally {
          this.dashboardService.show_loading.next(false);
        }
      }
    });
  }

  async updateNote(note: Note): Promise<any> {
    const data = await this.notesService.updateNote(
      this.company.uuid,
      note.uuid,
      note,
    );
    return data;
  }

  async updateClientInformation(
    clientInformationContent: string,
  ): Promise<any> {
    const data = await this.notesService.updateClientInformation(
      this.company.uuid,
      clientInformationContent,
    );
    return data;
  }

  async updateNoteOrClientInformation(data: UpdateNoteSubject): Promise<void> {
    this.dashboardService.show_loading.next(true);

    try {
      let _message;

      if (data.note) {
        const { message, note } = await this.updateNote(data.note);
        _message = message;
        this.notes = this.notes.map((_note) =>
          _note.uuid === note.uuid ? note : _note,
        );
        this.sortNotes();
        this.cdr.detectChanges();
      } else if (data.clientInformationContent) {
        const { message, client_information } =
          await this.updateClientInformation(data.clientInformationContent);
        _message = message;
        this.clientInformation = client_information;
      }
      this.toastr.success(`${_message}`);
    } catch (error) {
      this.handleErrorCatch(error);
    } finally {
      this.dashboardService.show_loading.next(false);
    }
  }

  async reload(): Promise<void> {
    this.loading = true;

    const { notes, client_information } = await this.getAllCompanyNotes();
    this.notes = notes;
    this.clientInformation = client_information;
    this.sortNotes();

    this.loading = false;
    this.cdr.detectChanges();
  }

  sortNotes(): void {
    if (!this.notes) return;

    switch (this.sortBy) {
      case 'newest':
        this.notes.sort((a, b) => new Date(b.created_dt).getTime() - new Date(a.created_dt).getTime());
        break;
      case 'oldest':
        this.notes.sort((a, b) => new Date(a.created_dt).getTime() - new Date(b.created_dt).getTime());
        break;
      case 'title':
        this.notes.sort((a, b) => a.title.localeCompare(b.title));
        break;
    }
    this.cdr.detectChanges();
  }

  handleErrorCatch({ error }): void {
    console.log({ error });
    this.toastr.error(`${error.message}`);
  }

  togglePanel(event) {
    var target = event.target || event.srcElement || event.currentTarget;
    target.closest('app-notes').classList.toggle("basis-auto");
    this.isPanelHidden = !this.isPanelHidden;
  }

  getSortLabel(): string {
    switch (this.sortBy) {
      case 'newest':
        return 'Date (Newest)';
      case 'oldest':
        return 'Date (Oldest)';
      case 'title':
        return 'Title';
      default:
        return 'Date (Newest)';
    }
  }

  changeSort(sortBy: string): void {
    this.sortBy = sortBy;
    this.sortNotes();
  }
}
